import type { PersistedClient } from '@tanstack/query-persist-client-core'
import { createStore, get, keys, promisifyRequest } from 'idb-keyval'

import type { CacheGeneration } from './cache-scope'
import type {
  AdoptedWriter,
  SnapshotPersister,
  WriterIndex,
} from './writer-snapshots'
import { cacheKeyOwner, cacheUserPrefixes } from './cache-scope'
import {
  describeWriterSnapshot,
  isScopeWriterKey,
  isSupersededWriter,
  parseWriterIndex,
  writerIndexKey,
} from './writer-snapshots'

const ownedStore = createStore('keyval-store', 'keyval')
const deletedKey = (owner: string) => ['deleted-cache-owner-v1', owner]
const generationKey = (owner: string) => ['cache-owner-generation-v1', owner]
const forgottenKey = (owner: string, operation: string) => [
  'forgotten-cache-operation-v1',
  owner,
  operation,
]

export async function deleteIndexedDBUserCaches(userId: string): Promise<void> {
  const prefixes = cacheUserPrefixes(userId)
  await ownedStore('readwrite', store => {
    store.put(true, deletedKey(userId))
    const cursor = store.openCursor()
    cursor.onsuccess = () => {
      const row = cursor.result
      if (!row) return
      if (
        typeof row.key === 'string' &&
        prefixes.some(prefix => row.key.toString().startsWith(prefix))
      )
        row.delete()
      row.continue()
    }
    return promisifyRequest(store.transaction)
  })
}

export async function forgetIndexedDBUserCaches(
  userId: string,
  operationId: string,
): Promise<void> {
  if (!userId || !operationId || operationId.length > 128)
    throw new Error('Cache removal requires an owner and operation')
  const prefixes = cacheUserPrefixes(userId)
  const db = await ownedStore('readonly', store => store.transaction.db)
  const tx = db.transaction('keyval', 'readwrite', { durability: 'strict' })
  const store = tx.objectStore('keyval')
  await (() => {
    const prior = store.get(forgottenKey(userId, operationId))
    prior.onsuccess = () => {
      if (prior.result !== undefined) {
        if (prior.result !== true) store.transaction.abort()
        return
      }
      store.put(true, forgottenKey(userId, operationId))
      store.put(operationId, generationKey(userId))
      const cursor = store.openCursor()
      cursor.onsuccess = () => {
        const row = cursor.result
        if (!row) return
        if (
          typeof row.key === 'string' &&
          prefixes.some(prefix => row.key.toString().startsWith(prefix))
        )
          row.delete()
        row.continue()
      }
    }
    return promisifyRequest(store.transaction)
  })()
}

export async function removeIndexedDBCaches(
  prefix: string,
  expected?: CacheGeneration,
): Promise<void> {
  await ownedStore('readwrite', store => {
    const remove = () => {
      const cursor = store.openCursor()
      cursor.onsuccess = () => {
        const row = cursor.result
        if (!row) return
        if (typeof row.key === 'string' && row.key.startsWith(prefix))
          row.delete()
        row.continue()
      }
    }
    if (expected) {
      const generation = store.get(generationKey(expected.ownerId))
      generation.onsuccess = () => {
        if ((generation.result ?? null) === expected.generation) remove()
      }
    } else remove()
    return promisifyRequest(store.transaction)
  })
}

/** Older snapshots without an index entry are read inline only in small numbers. */
const MAX_INLINE_UNINDEXED = 4

const yieldToMain = () =>
  new Promise<void>(resolve => {
    if (typeof requestIdleCallback === 'function')
      requestIdleCallback(() => resolve(), { timeout: 1000 })
    else setTimeout(resolve, 0)
  })

/** Run only while the owner is neither deleted nor rotated to a new generation. */
function whenOwnerCurrent(
  store: IDBObjectStore,
  owner: string | null,
  generation: unknown,
  run: () => void,
) {
  if (!owner) {
    run()
    return
  }
  const marker = store.get(deletedKey(owner))
  marker.onsuccess = () => {
    if (marker.result !== undefined) return
    const current = store.get(generationKey(owner))
    current.onsuccess = () => {
      if ((current.result ?? null) === generation) run()
    }
  }
}

export async function createIndexedDBPersister(
  cacheKey: string,
  scopePrefix = cacheKey,
  { maxInlineUnindexed = MAX_INLINE_UNINDEXED } = {},
): Promise<SnapshotPersister> {
  const owner = cacheKeyOwner(cacheKey)
  const generation: unknown = owner
    ? ((await get(generationKey(owner), ownedStore)) ?? null)
    : null
  if (generation !== null && typeof generation !== 'string')
    throw new Error('Cache generation is invalid')
  const current = async () =>
    !owner ||
    ((await get(deletedKey(owner), ownedStore)) === undefined &&
      ((await get(generationKey(owner), ownedStore)) ?? null) === generation)
  const indexKey = scopePrefix === cacheKey ? null : writerIndexKey(scopePrefix)
  // Learned during restore, folded into the shared index on the first write.
  let adopted: AdoptedWriter | null = null
  let discovered: WriterIndex = {}
  let missing: WriterIndex = {}
  let backlog: string[] = []
  let sweep: Promise<void> | null = null

  /** Read pre-index snapshots one task at a time, after this writer's own
   * snapshot is durable, so a large backlog never blocks a page load. */
  const sweepBacklog = async (keysToSweep: string[], ceiling: number) => {
    for (const key of keysToSweep) {
      await yieldToMain()
      if (!indexKey || !(await current())) return
      const value = await get<PersistedClient>(key)
      if (!value) continue
      const entry = describeWriterSnapshot(value)
      let isStopped = false
      await ownedStore('readwrite', store => {
        whenOwnerCurrent(store, owner, generation, () => {
          // A retired scope has no own snapshot; never recreate its index.
          const own = store.count(cacheKey)
          own.onsuccess = () => {
            if (own.result === 0) {
              isStopped = true
              return
            }
            const request = store.get(indexKey)
            request.onsuccess = () => {
              const index = parseWriterIndex(request.result)
              if (index[key]) return
              if (entry.mutations === 0 && entry.timestamp <= ceiling)
                store.delete(key)
              else {
                index[key] = entry
                store.put(index, indexKey)
              }
            }
          }
        })
        return promisifyRequest(store.transaction)
      })
      if (isStopped) return
    }
  }

  return {
    persistClient: async (client: PersistedClient) => {
      try {
        const own = describeWriterSnapshot(client)
        let isWritten = false
        await ownedStore('readwrite', store => {
          whenOwnerCurrent(store, owner, generation, () => {
            store.put(client, cacheKey)
            isWritten = true
            if (!indexKey) return
            // One transaction: concurrent tabs serialize their index updates.
            const request = store.get(indexKey)
            request.onsuccess = () => {
              const index = parseWriterIndex(request.result)
              for (const [key, entry] of Object.entries(missing))
                if (index[key]?.timestamp === entry.timestamp) delete index[key]
              for (const [key, entry] of Object.entries(discovered))
                index[key] ??= entry
              index[cacheKey] = own
              for (const [key, entry] of Object.entries(index)) {
                if (key === cacheKey) continue
                if (isSupersededWriter(key, entry, own, adopted)) {
                  store.delete(key)
                  delete index[key]
                }
              }
              store.put(index, indexKey)
            }
          })
          return promisifyRequest(store.transaction)
        })
        if (!isWritten) return false
        adopted = null
        discovered = {}
        missing = {}
        if (backlog.length && !sweep) {
          const keysToSweep = backlog
          backlog = []
          sweep = sweepBacklog(keysToSweep, own.timestamp).catch(() =>
            console.warn('[QueryCache:IDB] Failed to index older snapshots'),
          )
        }
        return true
      } catch {
        console.warn('[QueryCache:IDB] Failed to persist client')
        return false
      }
    },
    restoreClient: async () => {
      try {
        if (!(await current())) return undefined
        const saved = await get<PersistedClient>(cacheKey)
        if (saved) return (await current()) ? saved : undefined
        // Migrate only this exact account/org/access scope. Superseded
        // snapshots are pruned by this writer's first successful write; one
        // holding queued offline commands is kept unless it is adopted here.
        const [allKeys, rawIndex] = await Promise.all([
          keys(),
          indexKey ? get(indexKey) : undefined,
        ])
        const index = parseWriterIndex(rawIndex)
        const present = new Set(
          allKeys.filter(key => isScopeWriterKey(key, scopePrefix, cacheKey)),
        )
        missing = Object.fromEntries(
          Object.entries(index).filter(
            ([key]) => key !== cacheKey && !present.has(key),
          ),
        )
        const candidates: Array<{
          key: string
          timestamp: number
          value?: PersistedClient
        }> = []
        const unindexed: string[] = []
        for (const key of present) {
          if (index[key]) candidates.push({ key, ...index[key] })
          else unindexed.push(key)
        }
        if (unindexed.length <= maxInlineUnindexed) {
          const values = await Promise.all(
            unindexed.map(key => get<PersistedClient>(key)),
          )
          values.forEach((value, position) => {
            if (!value) return
            const key = unindexed[position]
            discovered[key] = describeWriterSnapshot(value)
            candidates.push({ key, ...discovered[key], value })
          })
        } else backlog = unindexed
        candidates.sort((a, b) => b.timestamp - a.timestamp)
        for (const candidate of candidates) {
          const value =
            candidate.value ?? (await get<PersistedClient>(candidate.key))
          if (!value) continue
          if (!(await current())) return undefined
          adopted = {
            key: candidate.key,
            timestamp: describeWriterSnapshot(value).timestamp,
          }
          return value
        }
        return undefined
      } catch {
        console.warn('[QueryCache:IDB] Failed to restore client')
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await removeIndexedDBCaches(
          scopePrefix,
          owner
            ? { ownerId: owner, generation: generation as string | null }
            : undefined,
        )
      } catch {
        console.warn('[QueryCache:IDB] Failed to remove client')
      }
    },
  }
}
